import { type GradeCategory } from "@core/grades/types";
import { URLS } from "@core/shared/utils";
import { BaseLink } from "@design-system/components/link";
import { trans, plainTrans } from "@mongez/localization";
import {
  ArrowUpIcon,
  BookOpenIcon,
  FolderIcon,
  SparklesIcon,
} from "lucide-react";
import { type ReactNode } from "react";

type CategoryChapterMapProps = {
  categories: GradeCategory[];
};

const chapterAccents = [
  "from-violet-500 to-fuchsia-500",
  "from-cyan-500 to-blue-500",
  "from-amber-500 to-orange-500",
  "from-emerald-500 to-teal-500",
];

export function CategoryChapterMap({ categories }: CategoryChapterMapProps) {
  if (!categories.length) return null;

  return (
    <section className="mt-10">
      <SectionHeading
        icon={<FolderIcon className="h-5 w-5" />}
        title={plainTrans("categoryDetails.childChapters")}
        description={trans("categoryDetails.childChaptersHint")}
      />

      <div className="mt-5 grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
        {categories.map((category, index) => {
          const lessonsCount =
            category.lessons?.length || category.total?.lessons || 0;
          const accent = chapterAccents[index % chapterAccents.length];

          return (
            <BaseLink
              key={category.id}
              to={URLS.grades.viewCategory(category)}
              className="hover:shadow-primary-950/10 group relative overflow-hidden rounded-[1.5rem] border border-gray-200 bg-white p-5 shadow-sm transition duration-300 hover:-translate-y-1 hover:border-primary-200 hover:no-underline hover:shadow-xl dark:border-gray-800 dark:bg-gray-900 dark:hover:border-primary-800"
            >
              <div
                className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${accent}`}
              />
              <div className="flex items-start gap-4">
                <span
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br text-lg font-black text-white shadow-lg ${accent}`}
                >
                  {String(index + 1).padStart(2, "0")}
                </span>
                <div className="min-w-0 flex-1">
                  <h3 className="line-clamp-2 text-base font-black leading-6 text-gray-950 transition group-hover:text-primary-700 dark:text-white dark:group-hover:text-primary-300">
                    {category.name}
                  </h3>
                  <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-semibold text-gray-500 dark:text-gray-400">
                    <span className="inline-flex items-center gap-1">
                      <BookOpenIcon className="h-3.5 w-3.5" />
                      {trans("categoryDetails.lessonCount", {
                        count: lessonsCount,
                      })}
                    </span>
                    {!lessonsCount ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-1 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
                        <SparklesIcon className="h-3 w-3" />
                        {trans("categoryDetails.chapterComingSoon")}
                      </span>
                    ) : null}
                  </div>
                </div>
                <ArrowUpIcon className="h-5 w-5 shrink-0 rotate-45 text-primary-500 transition group-hover:-translate-y-1 group-hover:translate-x-1 rtl:group-hover:-translate-x-1" />
              </div>
              <span className="mt-5 inline-flex items-center text-xs font-black text-primary-700 dark:text-primary-300">
                {trans("categoryDetails.openChapter")}
              </span>
            </BaseLink>
          );
        })}
      </div>
    </section>
  );
}

export function SectionHeading({
  description,
  icon,
  title,
}: {
  description: string;
  icon: ReactNode;
  title: string;
}) {
  return (
    <div className="flex items-start gap-3">
      <span className="dark:bg-primary-950/50 flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary-50 text-primary-700 dark:text-primary-300">
        {icon}
      </span>
      <div>
        <h2 className="text-xl font-black text-gray-950 dark:text-white sm:text-2xl">
          {title}
        </h2>
        <p className="mt-1 text-sm leading-6 text-gray-600 dark:text-gray-300">
          {description}
        </p>
      </div>
    </div>
  );
}
